import React, { useState } from 'react';
import { Modal, Box, Button, TextField, Checkbox, FormControlLabel } from '@mui/material';

const ConfigModal = ({
  open,
  handleClose,
  template,
  createEnv,
  templateInputConfigs,
  onModalCreate
}) => {
  const isVscodeRelated = ['vscode-python', 'vscode-nodejs'].includes(template);

  const initialState = templateInputConfigs.reduce((acc, field) => {
    acc[field] = '';
    return acc;
  }, {});

  const [formValues, setFormValues] = useState(initialState);
  const [repoError, setRepoError] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));

    if (field.includes('repository')) {
      const githubRepoRegex = /^https:\/\/github\.com\/[a-zA-Z0-9-]+\/[a-zA-Z0-9-_.]+$/;
      setRepoError(value && !githubRepoRegex.test(value) ? 'Invalid GitHub repository URL' : '');
    }
  };

  const handleCheckboxChange = (event) => {
    setIsCustom(event.target.checked);
  };

  const handleCreateEnv = async () => {
    let requiredFields = templateInputConfigs;
    if (isVscodeRelated && !isCustom) {
      requiredFields = templateInputConfigs.filter(field =>
        !(field.includes('repository') || field.includes('branch'))
      );
    }
    const isFormComplete = requiredFields.every(field => formValues[field]?.trim() !== '');
    if (repoError || !isFormComplete) return;

    const inputs = { ...formValues };
    if (isVscodeRelated && !isCustom) {
      templateInputConfigs.forEach(field => {
        if (field.includes('repository')) {
          inputs[field] = 'https://github.com/ranjithkumar-ps/empty-repo.git';
        }
        if (field.includes('branch')) {
          inputs[field] = 'main';
        }
      });
    }

    if (onModalCreate) {
      onModalCreate({
        templateId: template._id,
        templateName: template.templateName,
        inputs,
      });
    }

    try {
      await createEnv({
        _id: template._id,
        templateName: template.templateName,
        inputs,
      });
    } catch (error) {
      console.error('DEBUG: Error creating environment:', error);
    }

    handleClose();
  };

  const handleCloseModal = () => {
    setFormValues(initialState);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleCloseModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          p: 4,
          borderRadius: '4px',
          border: '0.936px solid rgba(255, 255, 255, 0.20)',
          background: '#1A1D21',
        }}
      >
        <h2 style={{
          color: '#FFF',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '24px', // 150%
          letterSpacing: '-1px',
        }}>
          Enter Details for {template?.templateName || ''}
        </h2>

        {/* Render checkbox only for vscode-related templates */}
        {isVscodeRelated && templateInputConfigs.some(field => field.includes('repository') || field.includes('branch')) && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isCustom}
                onChange={handleCheckboxChange}
                sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
              />
            }
            label={<span style={{ color: 'white' }}>Clone a Repository</span>}
          />
        )}

        {templateInputConfigs.map((field) => {
          // For vscode variants, if custom is not enabled, skip showing repository and branch fields.
          if (isVscodeRelated && !isCustom && (field.includes('repository') || field.includes('branch'))) {
            return null;
          }
          const fieldName = field.split('.').pop();
          const label = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

          return (
            <Box key={field} sx={{ mt: 2 }}>
              <div
                style={{
                  color: '#FFF',
                  fontFamily: 'Plus Jakarta Sans',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '24px', // 200%
                  letterSpacing: '-0.5px',
                  marginBottom: '2px'
                }}
              >
                {label}
              </div>
              <TextField
                fullWidth
                value={formValues[field] || ''}
                onChange={handleInputChange(field)}
                error={field.includes('repository') && !!repoError}
                helperText={field.includes('repository') ? repoError : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    color: 'white',
                    background: '#000',
                    borderRadius: '5px',
                    opacity: 0.2,
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#00FF85',
                      borderWidth: '1px',
                      borderRadius: '5px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#00FF85',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00FF85',
                    },
                  },
                  '& .MuiInputBase-input': { color: 'white' },
                }}
                InputLabelProps={{ shrink: false }}
              />
            </Box>
          );
        })}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleCreateEnv}
            variant="contained"
            disabled={
              repoError ||
              (isVscodeRelated
                ? (isCustom
                  ? templateInputConfigs.some(field => !formValues[field]?.trim())
                  : templateInputConfigs
                    .filter(field => !(field.includes('repository') || field.includes('branch')))
                    .some(field => !formValues[field]?.trim()))
                : templateInputConfigs.some(field => !formValues[field]?.trim())
              )}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(91deg, #00FF85 7.38%, #00E5FF 96.85%)',
              color: 'black',
              color: '#000',
              textAlign: 'center',
              fontFamily: 'Plus Jakarta Sans',
              fontSize: '12px',
              fontStyle: ' normal',
              fontWeight: '700',
              lineHeight: '18px', /* 150% */
              letterSpacing: '0.15px',
            }}
          >
            Create Environment
          </Button>
          <Button onClick={handleCloseModal}
            sx={{
              width: '96px',
              height: '34px',
              variant: 'contained',
              borderRadius: '5px',
              background: '#15171A',
              color: '#FFF',
              textAlign: 'center',
              fontFamily: 'Plus Jakarta Sans',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: '18px', /* 150% */
              letterSpacing: '0.15px',
            }}
          >

            Close
          </Button>
        </Box>
      </Box>
    </Modal >
  );
};

export default ConfigModal;
